import {
  createMobFromRecords,
  computeMobStateAtDate,
  type MobT,
  type ProjectedMobState,
} from '@ruminati/livestock-inventory/mob'
import { type LivestockMobT } from '@ruminati/types_v2/livestock_mobs'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useLivestockStore } from './livestock'

export const useMobsStore = defineStore('mobs', () => {
  const livestockStore = useLivestockStore()
  const mobs = ref<Record<string, Array<LivestockMobT>>>({})

  function setForProperty(propertyId: string, items: Array<LivestockMobT>): void {
    mobs.value[propertyId] = items
  }

  function addOrReplace(propertyId: string, item: LivestockMobT): void {
    const existing = mobs.value[propertyId]
    if (existing === undefined) {
      mobs.value[propertyId] = [item]
      return
    }
    const index = existing.findIndex((m) => m.id === item.id)
    if (index > -1) existing.splice(index, 1, item)
    else existing.push(item)
  }

  function remove(propertyId: string, id: string): void {
    const existing = mobs.value[propertyId]
    if (existing === undefined) return
    const index = existing.findIndex((m) => m.id === id)
    if (index > -1) existing.splice(index, 1)
  }

  // Hydrated MobT per property - a mob's headcount events live in the same
  // livestock_events stream cohorts use (tagged by mobId), so this reads off
  // livestockStore.events rather than fetching its own copy.
  const mobStates = computed<Record<string, MobT[]>>(() => {
    const out: Record<string, MobT[]> = {}
    for (const [propertyId, records] of Object.entries(mobs.value)) {
      out[propertyId] = records.map((mobRecord) =>
        createMobFromRecords({ mobRecord, mobEvents: livestockStore.events ?? [] }),
      )
    }
    return out
  })

  const mobStateToday = computed<Record<string, ProjectedMobState[]>>(() => {
    const today = new Date()
    const out: Record<string, ProjectedMobState[]> = {}
    for (const [propertyId, states] of Object.entries(mobStates.value)) {
      out[propertyId] = states.map((mob) =>
        computeMobStateAtDate({ mob, date: today, useAuthoritativeCurrentState: true }),
      )
    }
    return out
  })

  return { mobs, mobStates, mobStateToday, setForProperty, addOrReplace, remove }
})
